import { seriesColor } from '../constants/chartPalette';

import { colourForToken } from './widgetColorToken';
import { parseContractDate, WIDGET_LOCALE } from './widgetFormat.js';

/**
 * `series_chart` → what the plotted widgets draw (CT-5641).
 *
 * Four types share one output shape — line, bar, area and stacked bar — and it
 * is not the shape the drawings read. The contract describes a chart the way a
 * chart is defined:
 *
 * ```jsonc
 * { "x_axis": { "type": "TIME", "grain": "MONTH", "field_key": "month" },
 *   "y_axes": [ { "key": "LEFT", "format": { "style": "CURRENCY", "compact": true } } ],
 *   "series": [ { "key": "Norte", "label": "Norte", "y_axis": "LEFT",
 *                 "color_token": "CATEGORICAL_1",
 *                 "points": [ { "x": "2026-08-01", "y": 41000 }, … ] } ] }
 * ```
 *
 * The drawings read parallel arrays — `categories[]` and a `values[]` per
 * series — because that is what an SVG polyline wants. Converting is this
 * module's only job, and it lives here rather than in each widget so the four
 * cannot disagree about three things that are easy to get subtly wrong:
 *
 * **Alignment.** Points are keyed by `x`, and two series need not carry the
 * same ones — a region with no sales in September simply has no point for it.
 * Zipping the arrays by position would silently shift that series a month. So
 * the categories are the union of every `x` in order of first appearance, and
 * a series with no point there gets `null`, which the drawings already read as
 * a gap rather than as zero.
 *
 * **Colour.** `color_token` is the backend's answer, and it wins; a series
 * without one keeps the colour its position would have given it, so a chart
 * that gains a token later does not reshuffle.
 *
 * **«Others».** A series flagged `is_others` is the tail the query folded
 * together under `display.max_series`. It is not a category like the rest —
 * it is the absence of the others — so it is marked, and the drawings give it
 * the neutral colour and leave it out of the colour rotation.
 */

/** A TIME axis sends ISO dates; its labels are what a human reads on an axis. */
const TIME_LABEL = Object.freeze({
  HOUR: { hour: 'numeric' },
  DAY: { day: 'numeric', month: 'short' },
  WEEK: { day: 'numeric', month: 'short' },
  MONTH: { month: 'short' },
  QUARTER: { month: 'short', year: '2-digit' },
  YEAR: { year: 'numeric' },
});

const labelForX = (x, axis) => {
  if (axis?.type !== 'TIME') return String(x);
  // `parseContractDate`, not `new Date`: a bare `2026-08-01` is UTC midnight,
  // which renders as July for any reader west of Greenwich. See its note.
  const date = parseContractDate(x);
  if (!date) return String(x);
  return date.toLocaleDateString(WIDGET_LOCALE, TIME_LABEL[axis.grain] ?? TIME_LABEL.DAY);
};

/**
 * @param {object|null} data A type's `data`, shaped as `series_chart`.
 * @param {object} [options]
 * @param {string} [options.accent] Overrides the first series' colour.
 * @returns {{
 *   categories: string[],
 *   series: Array<{ key: string, label: string, values: Array<number|null>, color: string, isOthers: boolean }>,
 *   format: object|null,
 *   axis: object|null,
 * }|null} `null` when there is no chart to draw.
 */
export function seriesChartToPlot(data, { accent } = {}) {
  const rawSeries = data?.series;
  if (!Array.isArray(rawSeries) || !rawSeries.length) return null;

  // Union of every `x`, in order of first appearance. A series missing one
  // gets a gap, never a zero.
  const order = [];
  const seen = new Set();
  rawSeries.forEach((entry) => {
    (entry.points ?? []).forEach((point) => {
      const key = String(point.x);
      if (seen.has(key)) return;
      seen.add(key);
      order.push(point.x);
    });
  });

  // The colour rotation skips «others», so adding a tail does not reshuffle
  // the real categories.
  let rotation = 0;

  const series = rawSeries.map((entry) => {
    const byX = new Map((entry.points ?? []).map((point) => [String(point.x), point.y]));
    const isOthers = Boolean(entry.is_others);
    const positional = isOthers ? undefined : seriesColor(rotation++, accent);

    return {
      key: entry.key ?? entry.label ?? String(rotation),
      label: entry.label ?? entry.key ?? '',
      values: order.map((x) => {
        const y = byX.get(String(x));
        return y === undefined ? null : y;
      }),
      color: entry.color_token ? colourForToken(entry.color_token, positional) : positional ?? colourForToken('NEUTRAL'),
      isOthers,
    };
  });

  return {
    categories: order.map((x) => labelForX(x, data.x_axis)),
    series,
    // One format for the plot: a chart with two y axes formats per axis, and
    // the left one is what the labels follow.
    format: data.y_axes?.[0]?.format ?? null,
    axis: data.x_axis ?? null,
  };
}

/**
 * `SPARKLINE` publishes a single list of points rather than a `series_chart`,
 * so it is lifted into the same shape — one series, no legend, no axis.
 */
export function pointsToPlot(data, { accent } = {}) {
  const points = data?.points;
  if (!Array.isArray(points) || !points.length) return null;

  return {
    categories: points.map((point) => String(point.x)),
    series: [{
      key: 'sparkline',
      label: '',
      values: points.map((point) => point.y),
      color: seriesColor(0, accent),
      isOthers: false,
    }],
    format: data.format ?? null,
    axis: null,
  };
}

export default seriesChartToPlot;
